import React from 'react';

interface NumericInputProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'type'> {
  /** Hide +/- buttons (e.g. for filter inputs) */
  hideButtons?: boolean;
}

/**
 * Numeric input with custom +/- buttons and hidden native spinners.
 * Accepts `value` as string — empty string is valid (no stuck 0).
 */
const NumericInput: React.FC<NumericInputProps> = ({ hideButtons, ...rest }) => {
  const step = Number(rest.step) || 1;
  const min = rest.min !== undefined ? Number(rest.min) : undefined;
  const max = rest.max !== undefined ? Number(rest.max) : undefined;

  const currentValue = (): number => {
    const v = parseFloat(String(rest.value));
    return isNaN(v) ? (min ?? 0) : v;
  };

  const clamp = (n: number): number => {
    let result = n;
    if (min !== undefined && result < min) result = min;
    if (max !== undefined && result > max) result = max;
    // Round to avoid floating-point drift
    const decimals = (String(step).split('.')[1] || '').length;
    return parseFloat(result.toFixed(decimals));
  };

  const fireChange = (newValue: number) => {
    if (!rest.onChange) return;
    const val = String(clamp(newValue));
    const synth = {
      target: { value: val },
      currentTarget: { value: val },
      preventDefault: () => {},
      stopPropagation: () => {},
    } as unknown as React.ChangeEvent<HTMLInputElement>;
    rest.onChange(synth);
  };

  const decrement = () => {
    if (rest.disabled) return;
    fireChange(currentValue() - step);
  };

  const increment = () => {
    if (rest.disabled) return;
    fireChange(currentValue() + step);
  };

  if (hideButtons) {
    return (
      <input {...rest} type="number" className={['numeric-no-spinners', rest.className].filter(Boolean).join(' ')} />
    );
  }

  return (
    <div className="numeric-input-wrapper">
      <button
        type="button"
        className="numeric-btn numeric-btn--minus"
        onClick={decrement}
        tabIndex={-1}
        disabled={rest.disabled || (min !== undefined && currentValue() <= min)}
        aria-label="Decrease"
      >
        &minus;
      </button>
      <input {...rest} type="number" className={['numeric-input', rest.className].filter(Boolean).join(' ')} />
      <button
        type="button"
        className="numeric-btn numeric-btn--plus"
        onClick={increment}
        tabIndex={-1}
        disabled={rest.disabled || (max !== undefined && currentValue() >= max)}
        aria-label="Increase"
      >
        +
      </button>
    </div>
  );
};

export default NumericInput;
